import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, ArrowUpRight, X, Search } from "lucide-react";
import { toast } from "sonner";
import { createClient, updateClient, listJobs } from "@/lib/api";
import { ADMIN_CLIENTS } from "@/constants/testIds";

const EASE = [0.76, 0, 0.24, 1];

const formatDetail = (d) => {
  if (!d) return "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" · ");
  return String(d);
};

export default function ClientEditorModal({ open, initial, onClose, onSaved }) {
  const isEdit = Boolean(initial);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [password, setPassword] = useState("");
  const [assigned, setAssigned] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [jobQuery, setJobQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    listJobs().then(setJobs).catch(() => setJobs([]));
    if (initial) {
      setEmail(initial.email || "");
      setName(initial.name || "");
      setCompany(initial.company || "");
      setPassword("");
      setAssigned(initial.assigned_job_ids || []);
    } else {
      setEmail("");
      setName("");
      setCompany("");
      setPassword("");
      setAssigned([]);
    }
    setError("");
    setJobQuery("");
  }, [open, initial]);

  const toggleJob = (id) => {
    setAssigned((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      let saved;
      if (isEdit) {
        const payload = {
          name,
          company: company || null,
          assigned_job_ids: assigned,
        };
        if (password) payload.password = password;
        saved = await updateClient(initial.id, payload);
        toast.success("Client updated.");
      } else {
        if (password.length < 8) {
          setError("Password must be at least 8 characters.");
          setLoading(false);
          return;
        }
        saved = await createClient({
          email,
          password,
          name,
          company: company || null,
          assigned_job_ids: assigned,
        });
        toast.success(`${saved.name} added.`);
      }
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      setError(formatDetail(err?.response?.data?.detail) || err?.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredJobs = jobs.filter((j) => {
    if (!jobQuery.trim()) return true;
    const q = jobQuery.toLowerCase();
    return (
      j.title.toLowerCase().includes(q) ||
      j.company.toLowerCase().includes(q) ||
      j.location.toLowerCase().includes(q)
    );
  });

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[70] bg-[rgba(20,17,13,0.45)] backdrop-blur-sm flex items-start md:items-center justify-center px-4 py-16 overflow-y-auto"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="relative bg-[var(--bg)] border border-[var(--line-strong)] w-full max-w-3xl p-8 md:p-10"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onClose}
              data-testid={ADMIN_CLIENTS.editorCancel}
              className="absolute top-4 right-4 text-[var(--ink-70)] hover:text-[var(--ink)] p-1"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            <p className="overline mb-4">
              Hiring partner · {isEdit ? "Edit account" : "New account"}
            </p>
            <h2 className="font-display text-3xl md:text-4xl tracking-[-0.02em] leading-[1.05] text-[var(--ink)] mb-8">
              {isEdit ? (
                <>
                  Update{" "}
                  <span className="font-serif-accent italic text-[var(--accent)]">
                    {initial.name}
                  </span>
                </>
              ) : (
                <>
                  Grant{" "}
                  <span className="font-serif-accent italic text-[var(--accent)]">
                    access
                  </span>
                  .
                </>
              )}
            </h2>

            <form onSubmit={onSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="overline block mb-2">Email</label>
                  <input
                    required
                    type="email"
                    disabled={isEdit}
                    className="editorial-input disabled:opacity-60"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    data-testid={ADMIN_CLIENTS.editorEmail}
                  />
                </div>
                <div>
                  <label className="overline block mb-2">Full name</label>
                  <input
                    required
                    type="text"
                    className="editorial-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jane Doe"
                    data-testid={ADMIN_CLIENTS.editorName}
                  />
                </div>
                <div>
                  <label className="overline block mb-2">Company</label>
                  <input
                    type="text"
                    className="editorial-input"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    placeholder="Acme Corp"
                    data-testid={ADMIN_CLIENTS.editorCompany}
                  />
                </div>
                <div>
                  <label className="overline block mb-2">
                    {isEdit ? "New password (leave blank to keep)" : "Password"}
                  </label>
                  <input
                    type="password"
                    className="editorial-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={isEdit ? "••••••••" : "Min 8 characters"}
                    minLength={isEdit ? 0 : 8}
                    required={!isEdit}
                    data-testid={ADMIN_CLIENTS.editorPassword}
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-[var(--line-strong)]">
                <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                  <label className="overline">Assigned roles ({assigned.length})</label>
                  <div className="flex items-center gap-2 border border-[var(--line-strong)] px-2">
                    <Search className="w-3.5 h-3.5 text-[var(--ink-50)]" />
                    <input
                      type="text"
                      value={jobQuery}
                      onChange={(e) => setJobQuery(e.target.value)}
                      placeholder="Search roles…"
                      className="bg-transparent border-0 text-xs py-1.5 focus:outline-none w-52"
                    />
                  </div>
                </div>
                <div className="border border-[var(--line-strong)] max-h-64 overflow-y-auto">
                  {filteredJobs.map((j) => {
                    const on = assigned.includes(j.id);
                    return (
                      <button
                        type="button"
                        key={j.id}
                        onClick={() => toggleJob(j.id)}
                        data-testid={ADMIN_CLIENTS.editorJobToggle(j.id)}
                        className={`w-full text-left px-4 py-3 border-b border-[var(--line)] last:border-b-0 flex items-start gap-3 transition-colors ${
                          on ? "bg-[var(--bg-2)]" : "hover:bg-[var(--bg-2)]"
                        }`}
                      >
                        <span
                          className={`shrink-0 mt-0.5 w-4 h-4 border ${
                            on ? "bg-[var(--accent)] border-[var(--accent)]" : "border-[var(--line-strong)]"
                          }`}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-[var(--ink)] font-medium truncate">
                            {j.title}
                          </p>
                          <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--ink-70)]">
                            {j.company} · {j.location} · {j.remote_type}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                  {filteredJobs.length === 0 && (
                    <p className="p-4 text-sm text-[var(--ink-50)]">No matching roles.</p>
                  )}
                </div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--ink-50)] mt-2">
                  Tip: they&apos;ll also see any candidate whose current company matches the field above.
                </p>
              </div>

              {error && (
                <div
                  className="text-red-600 text-sm pt-2"
                  data-testid={ADMIN_CLIENTS.editorError}
                >
                  {error}
                </div>
              )}

              <div className="pt-6 flex items-center gap-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-pill disabled:opacity-60"
                  data-testid={ADMIN_CLIENTS.editorSave}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Saving…</span>
                    </>
                  ) : (
                    <>
                      <span>{isEdit ? "Save changes" : "Create account"}</span>
                      <ArrowUpRight className="w-4 h-4" />
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-sm uppercase tracking-[0.22em] text-[var(--ink-70)] hover:text-[var(--ink)] link-slide"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
